/**
 * This Layout is needed for Starter Kit.
 */

// Global
import { DesignLibrary, Page } from '@sitecore-content-sdk/nextjs';
import React from 'react';
import clsx from 'clsx';

// Local
import Scripts from 'src/Scripts';
import SitecoreStyles from 'components/content-sdk/SitecoreStyles';
import { PlaceholderWrapper } from './components/helpers/SitecoreWrappers/PlaceholderWrapper/PlaceholderWrapper';
import Metadata from 'components/helpers/Global/Metadata';
import { GoogleTagManager } from '@next/third-parties/google';
import { BrandAndThemeProvider } from 'components/helpers/ContextProvider/BrandAndThemeContext';
import CoreWebVitals from 'components/helpers/Global/Analytics/CoreWebVitals';
import UTag from 'components/helpers/Global/Analytics/UTag';
import { useDisabledFeatureFlags } from 'lib/hooks/useDisabledFeatureFlags';
import { useSiteSettings } from 'lib/hooks/useSiteSettings';
import { useGlobalData } from 'lib/hooks/sitecore/useGlobalData';
import OneTrustScript from 'components/authorable/shared/onetrust-script/OneTrustScript';
import { BazaarVoiceScript } from 'components/helpers/BazaarVoice';
import { OneTrustFilter } from 'components/helpers/OneTrust';
import { PageTypes } from '@generated/Project.GMI.model';
import { isTemplate } from 'lib/utils/sitecore/data-template';
import Shopping from 'components/helpers/Shopping/Shopping';
import { WakeLockProvider } from 'components/helpers/ActionToolbar/ActionButtons/KeepScreenOnToggle/WakeLockContext';
import { AdChoicesScript } from 'components/helpers/AdChoices/AdChoicesScript';
import { InlinedHtmlLink } from 'lib/page-props';
import { SpeedInsights } from '@vercel/speed-insights/next';

export interface LayoutProps {
  page: Page;
  headLinks?: InlinedHtmlLink[];
}

const Layout = ({ page, headLinks }: LayoutProps): JSX.Element => {
  const { layout, mode } = page;
  const { route } = layout.sitecore;
  const mainClassPageEditing = mode.isEditing ? 'editing-mode' : 'prod-mode';
  const importMapDynamic = () => import('.sitecore/import-map');
  const siteSettings = useSiteSettings() ?? {};
  const { siteName, brand } = useGlobalData();
  const googleTagManagerId = siteSettings?.gtmId?.value;

  const disabledFeatures = useDisabledFeatureFlags();

  if (!googleTagManagerId) {
    console.warn('Google Tag Manager ID is missing. Google Analytics will not be rendered.');
  }

  const loadBazaarVoiceScript = route
    ? [
        PageTypes.Constants.RecipeWildcardPage.TemplateId,
        PageTypes.Constants.ProductDetailPage.TemplateId,
      ].some((templateId) => isTemplate(route, templateId))
    : false;

  const isRecipePage =
    route && isTemplate(route, PageTypes.Constants.RecipeWildcardPage.TemplateId);

  /*
   * RENDERING
   */

  return (
    <>
      <BrandAndThemeProvider brand={brand} applyToBody>
        <WakeLockProvider>
          <Scripts />
          {headLinks && <SitecoreStyles headLinks={headLinks} />}
          {loadBazaarVoiceScript && (
            <OneTrustFilter
              defaultConsentPref="noconsent"
              optanonGroups={['2', '3', '4']}
              enablePlaceholder={false}
              filteredComponent={<BazaarVoiceScript />}
            />
          )}
          {route && <Metadata />}
          <CoreWebVitals cwvIndicator={siteName} />
          <UTag />
          {googleTagManagerId && !disabledFeatures.GTM && (
            <GoogleTagManager gtmId={googleTagManagerId} />
          )}
          <OneTrustScript />
          <SpeedInsights />
          <Shopping />
          <AdChoicesScript />
          <div className={clsx(mainClassPageEditing, { 'is-recipe': isRecipePage })}>
            {mode.isDesignLibrary ? (
              <DesignLibrary loadImportMap={importMapDynamic} />
            ) : (
              <>
                {route && (
                  <PlaceholderWrapper
                    helpTextHideIf={true}
                    name="headless-header"
                    rendering={route}
                  />
                )}
                <main>
                  {/* Hidden live region at start of <main> so NVDA cursor stays inside the main landmark when it fires */}
                  <div
                    id="sr-announcer"
                    role="status"
                    aria-live="assertive"
                    aria-atomic="true"
                    style={{
                      position: 'absolute',
                      width: '1px',
                      height: '1px',
                      padding: 0,
                      overflow: 'hidden',
                      clip: 'rect(0,0,0,0)',
                      whiteSpace: 'nowrap',
                      border: 0,
                    }}
                  />
                  <div id="content">
                    {route && (
                      <PlaceholderWrapper
                        helpTextHideIf={true}
                        name="headless-main"
                        rendering={route}
                      />
                    )}
                  </div>
                </main>
                <footer>
                  <div>
                    {route && (
                      <PlaceholderWrapper
                        helpTextHideIf={true}
                        name="headless-footer"
                        rendering={route}
                      />
                    )}
                  </div>
                </footer>
              </>
            )}
          </div>
        </WakeLockProvider>
      </BrandAndThemeProvider>
    </>
  );
};

export default Layout;
